import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  selectCurrentUserSubmittedLabResult,
  selectCurrentError,
  loadUserSubmittedLabResultById,
  selectLoading,
  loadBiomarkersConfig,
  selectBiomarkers,
  selectLoadingBiomarker,
  selectSavingSuccessful,
  selectAutomatedStatusChange,
  savingSuccessfulChange,
  selectShouldRedirect,
  setShouldRedirect,
} from '../../state';
import {
  BiomarkerData,
  UserSubmittedLabResultsDetailsResponse,
} from '../../../../interfaces/user-submitted-lab-result';
import UserSubmittedLabResultsTable from '../../components/UserSubmittedLabResultTable';
import Alert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';

export default function UserSubmittedLabResultDetailsPage() {
  const error = useSelector(selectCurrentError);
  const shouldRedirect = useSelector(selectShouldRedirect);
  const automatedStatusChange = useSelector(selectAutomatedStatusChange);
  const savingSuccessful = useSelector(selectSavingSuccessful);
  const { userId, resultId } = useParams<{ userId: string; resultId: string }>();
  const dispatch = useDispatch();
  const currentUserSubmittedLabResult = useSelector(selectCurrentUserSubmittedLabResult);
  const [reloadData, setReloadData] = useState(false);
  const [userSubmittedLabResult, setUserSubmittedLabResult] =
    useState<UserSubmittedLabResultsDetailsResponse | null>(currentUserSubmittedLabResult);
  const biomarkerConfig = useSelector(selectBiomarkers);
  const [biomarkers, setBiomarkers] = React.useState<BiomarkerData | null>(biomarkerConfig);
  const loadingDetails = useSelector(selectLoading);
  const loadingBiomarker = useSelector(selectLoadingBiomarker);
  const loading = loadingDetails || loadingBiomarker;
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const history = useHistory();

  useEffect(() => {
    if (currentUserSubmittedLabResult) {
      setUserSubmittedLabResult(currentUserSubmittedLabResult);
    }
  }, [currentUserSubmittedLabResult]);

  useEffect(() => {
    if (biomarkerConfig.biomarkers.length) {
      setBiomarkers(biomarkerConfig);
    }
  }, [biomarkerConfig]);

  useEffect(() => {
    dispatch(loadUserSubmittedLabResultById(userId, resultId));
    dispatch(loadBiomarkersConfig());
  }, [dispatch, userId, resultId]);

  useEffect(() => {
    setReloadData(false);
  }, []);

  useEffect(() => {
    if (savingSuccessful) {
      setAlertOpen(true);
      setAlertType('success');
      setAlertMessage('Saving was successful!');
      if (!automatedStatusChange && shouldRedirect) {
        dispatch(setShouldRedirect(false));
        history.push(`/user-submitted-lab-results`);
      }
    } else if (error) {
      setAlertOpen(true);
      setAlertType('error');
      setAlertMessage(`An error occurred while submitting: ${error}`);
      dispatch(setShouldRedirect(false));
    }
  }, [savingSuccessful, error]);

  if (loading || !currentUserSubmittedLabResult || !biomarkerConfig) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <div style={{ marginLeft: '32px', marginRight: '32px' }}>
        {!automatedStatusChange &&
          alertType === 'error' &&
          currentUserSubmittedLabResult.status !== 'rejected' && (
            <Collapse in={alertOpen}>
              <Alert
                severity={alertType}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setAlertOpen(false);
                      setAlertMessage('');
                      dispatch(savingSuccessfulChange(false));
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {alertMessage}
              </Alert>
            </Collapse>
          )}
        <UserSubmittedLabResultsTable
          biomarkerData={biomarkerConfig}
          userSubmittedLabResult={currentUserSubmittedLabResult}
        />
      </div>
    </React.Fragment>
  );
}
